import {
    FC,
    ReactElement, memo, useEffect, useMemo, useState
} from "react";
import { SquareButton } from "@themuse/design-system";
import { getActiveVariant, useExperimentState } from "@themuse/experiment-client";
import { getApplyButtonText } from "utils/ApplyButtonTextExperiment";
import { CheckMark } from "@themuse/design-system/lib/components/Icons";
import { appCastJobApply } from "../utils/appcast";
import { IMetadataObj } from "../utils/strings";
import { externalJobApply } from "../utils/externalJobApply";
import {
    getJobLastInteraction, setInteractionViewed, getInteractionDate, getApplicant
} from "../utils/interactionHistory";

import styles from "./JobApplyButton.module.scss";
import { useAppState } from "hooks/AppContext";

interface JobApplyButtonProps {
    jobId: number;
    setIsOpen?: (x: boolean) => void;
    className?: string;
    trackingProps?: IMetadataObj;
    isDirectApply: boolean;
}

/**
 * Renders a message indicating that the job has been applied.
 *
 * @param {Object} props - The component props.
 * @param {number} props.interactionDate - The date of the interaction in milliseconds.
 * @returns {ReactElement} - The rendered component.
 */
export const AppliedMsg = (props: { interactionDate?: number; }): ReactElement => {
    const { interactionDate } = props;
    return (
        <p className={styles.jobInteractionHistoryApply}>
            Applied {getInteractionDate(interactionDate)}
            <CheckMark />
        </p>
    );
};

/**
 * Renders a message indicating whether the user should use their saved information and resume.
 * @returns {ReactElement} - The rendered component or null.
 */
const StoredMsg: FC = memo((): ReactElement => (
    <p className={styles.jobInteractionUserData}>Use your saved info and resume</p>
));
StoredMsg.displayName = "StoredMsg";

/**
 * Renders a button component for job application.
 *
 * @param {JobApplyButtonProps} props - The props for the JobApplyButton component.
 * @param {string} props.className - The class name for the button component.
 * @param {number} props.jobId - The ID of the job.
 * @param {IMetadataObj} props.trackingProps - The tracking properties for the button.
 * @param {(x: boolean) => void} props.setIsOpen - The function to set the open state of a modal.
 * @param {boolean} props.isDirectApply - { deprecated } A flag indicating if it is a direct apply button.
 * @returns {ReactElement} The rendered JobApplyButton component.
 */
export const JobApplyButton = (props: JobApplyButtonProps): ReactElement => {
    const { job } = useAppState();
    const { state } = job;
    const {
        className,
        jobId,
        trackingProps,
    } = props;

    const [redirectUrl, setRedirectUrl] = useState<string>("");
    const [interactionDate, setInteractionDate] = useState<number>(null);
    const [showApplied, setShowApplied] = useState<boolean>(false);
    const [showStored, setShowStored] = useState<boolean>(false);
    const { experiments } = useExperimentState();
    const buttonText = useMemo(() => {
        return getApplyButtonText(state, '', experiments, getActiveVariant);
    }, [state, experiments]);
    const targetPageSection: string = "off_site";
    const trackingClass: string = "track-job-apply-button";
    const snowplowTrackingProps = {
        sp_click_button_text: trackingProps?.sp_click_button_text ?? buttonText,
        sp_click_button_type: trackingProps?.sp_click_button_type ?? "apply",
        sp_click_component: "button",
        sp_click_position: trackingProps?.sp_click_position ?? "bottom",
        sp_click_target_page_section: trackingProps?.sp_click_target_page_section ?? targetPageSection,
        sp_experiment_experiment_id: trackingProps?.sp_experiment_experiment_id ?? null,
        sp_experiment_variant_id: trackingProps?.sp_experiment_variant_id ?? null,
    };

    /**
     * Handles the click event for the job apply button.
     */
    const linkHandler = (): void => {
        externalJobApply(trackingProps.company_id, trackingProps.jobId);
        appCastJobApply();
        window.open(redirectUrl, "_blank");
    };

    const btnProps: Record<string, string | boolean | Function> = {
        className: `${className} ${trackingClass}`,
        onClick: linkHandler,
        color: "secondary-100",
        "data-tracking-event-type": "click",
        "data-tracking-props": JSON.stringify(snowplowTrackingProps),
        target: "_blank"
    };

    if (showApplied) {
        btnProps.disabled = true;
    }

    useEffect(() => {
        setRedirectUrl(`/job/redirect/${jobId}${window.location.search}`);
        setInteractionViewed(jobId);
        /**
         * Retrieves the apply status for a job.
         */
        const checkApplyStatus = (): void => {
            const lastInteraction = getJobLastInteraction(jobId);
            const isApplicant = getApplicant();
            if (lastInteraction) {
                setInteractionDate(lastInteraction?.dt);
                setShowApplied(lastInteraction?.app);
                setShowStored(isApplicant && !lastInteraction?.app);
            }
        };

        checkApplyStatus();

        // watch for storage changes
        window.addEventListener("storage", checkApplyStatus);
        return () => {
            window.removeEventListener("storage", checkApplyStatus);
        };
    }, [interactionDate, jobId]);

    return (
        <>
            <SquareButton {...btnProps}><span>{showApplied ? "APPLIED" : buttonText}</span></SquareButton>
            {showStored && <StoredMsg />}
            {showApplied && <AppliedMsg {...{ interactionDate }} />}
        </>
    );
};
